<template>
  <!------------------------------------------
    This component displays the congressional district profiles for GRPA to give to representatives.
  -------------------------------------------->
  <div id="congressional-district-profiles">
    <PageTitle v-if="embedded === false" title="Congressional District Snapshots"/>

    <SideMarginsWrapper maxWidth="1300">
      <AboutResource v-if="embedded === false" 
        class="mt-4"
        :updated="updated"
        :techContact1="{ name: 'Jordan', email: 'laytonj@lsc.gov', department: 'ODGA' }"
      >
        <p>
          This page provides a high-level overview of civil legal needs and services in the selected congressional district. It is also available on (and thus styled to match) LSC.gov.
        </p>
        <p>
          The data presented will vary from district to district depending on what is available and compelling.
        </p>
      </AboutResource>

      <div class="lsc-dot-gov-styling no-print">
        <div class="select-menus select-menus-left">
          <div class="header-and-menu">
            <div class="select-header"><label for="cd-state-select">Select a state</label></div>
            <StateSelect 
              :statePassedAsProp="selectedState" 
              @stateSelected="handleStateChange" 
              :lscDotGovVersion="true" 
              class="no-print" 
              selectMenuId="cd-state-select"
              :includePR="true"
              :includeAS="false"
              :includeVI="false"
              :includeMP="false"
              :includeGU="false" 
            />
          </div>
          <div class="header-and-menu" v-if="selectedState">
            <div class="select-header"><label for="cd-cd-select">Select a congressional district</label></div>
            <CDSelect @cdSelected="handleCdChange" :stFipsProp="selectedState" :lscDotGovVersion="true" :key="selectedState" class="no-print" selectMenuId="cd-cd-select" />
          </div>
        </div>
      </div>

      <div v-if="(selectedCDFips === '' || selectedCDFips === null || selectedCDFips === undefined) && embedded === true" class="no-selection-text">
        Please select a state and congressional district above to see a snapshot of civil legal needs and services, such as:
        <ul>
          <li>The gap between the civil legal needs of low-income residents and the resources available to meet those needs,</li>
          <li>Housing issues facing constitutents,</li>
          <li>The estimated number of people, seniors, veterans, and DV survivors served in the district, and</li>
          <li>The LSC grantee(s) serving the district, as well as nearby offices and top legal problems of civil legal aid cases.</li>
        </ul>
      </div>

      <div v-if="(selectedCDFips === '' || selectedCDFips === null || selectedCDFips === undefined) && embedded === false">
        <AlertSelectGrantee
          text="Please select a state and congressional district above"
          maxWidth="300"
        />
      </div>

      <div v-else-if="(selectedCDFips !== '' && selectedCDFips !== null && selectedCDFips !== undefined) && loading">
        <LoadingSpinner />
      </div>

      <div v-if="profileData !== null && selectedCDFips !== null && selectedCDFips !== undefined && loading === false">
        <div id="print-version-switch" v-if="embedded === false" class="no-print">
          <v-switch
            v-model="printSwitch"
            label="Show as one-pager"
            class="no-print"
          ></v-switch>
        </div>

        <!-- WEB LAYOUT -->
        <CDProfilesWeb
          v-if="printSwitch === false"
          :key="selectedCDFips" 
          :profileData="profileData" 
          :selectedCDFips="selectedCDFips"
          :selectedCDLabel="selectedCDLabel"
          :granteesByType="granteesByType"
          :officesPerGrantee="officesPerGrantee"
          :granteesInState="granteesInState"
        />

        <!-- PRINT LAYOUT -->
        <CDProfilesPrint
          v-if="printSwitch === true" 
          :key="selectedCDFips" 
          :profileData="profileData" 
          :selectedCDFips="selectedCDFips"
          :selectedCDLabel="selectedCDLabel"
          :granteesByType="granteesByType"
          :officesPerGrantee="officesPerGrantee"
          :granteesInState="granteesInState"
        />
      </div>
    </SideMarginsWrapper>
  </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import CDSelect from '@/components/select/CDSelect'
import StateSelect from '@/components/select/StateSelect'
import AboutResource from '@/components/common/AboutResource'
import AlertSelectGrantee from '@/components/common/AlertSelectGrantee'
import SideMarginsWrapper from '@/components/layout/SideMarginsWrapper'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import CDProfilesPrint from '@/views/advocacy/congress/cd_profiles/CDProfilesPrint'
import CDProfilesWeb from '@/views/advocacy/congress/cd_profiles/CDProfilesWeb'
import { servAreas2024 } from '@/js/serv_areas.js'
import { initPostHeight } from '@/js/iframe_postmessage.js'

export default {
  name: 'CDProfiles',

  components: { SideMarginsWrapper, AboutResource, AlertSelectGrantee, LoadingSpinner, PageTitle, CDSelect, StateSelect, CDProfilesPrint, CDProfilesWeb },

  data () {
    return {
      embedded: true,
      apiRoute: `${process.env.VUE_APP_BACKEND_NEW_DATA_API}`,
      loading: true,
      profileData: null,
      printSwitch: false,
      updated: '',
      officesPerGrantee: {},
      selectedCDFips: null,
      selectedCDLabel: null,
      selectedState: null,
      servAreas: []
    }
  },

  computed: {
    areaIsSelected () { return this.selectedCDFips !== '' && this.selectedCDFips !== null && this.selectedCDFips !== undefined && this.selectedCDFips !== '*' },
    stateFips () {
      return this.selectedCDFips !== null ? this.selectedCDFips.substring(0,2) : null
    },
    granteesInState () {
      return this.state !== null ? servAreas2024.filter(x => x.states.indexOf(this.stateFips) > -1) : null
    },
    granteesByType () {
      let bfGeneral = this.granteesInState.filter(x => x.serv_area_type === 'Basic Field' && this.servAreas.indexOf(x.serv_area) > -1).sort((a, b) => a.org_name.localeCompare(b.org_name))
      let agWorker = this.granteesInState.filter(x => x.serv_area_type === 'Agricultural Worker').sort((a, b) => a.org_name.localeCompare(b.org_name))

      return {
        bfGeneral: bfGeneral,
        agWorker: agWorker
      }
    }
  },

  created () {
    if (this.areaIsSelected) {
      this.initGetData()
    }
  },
  
  mounted () {
    if (this.$route.query.print && this.$route.query.print === 'true') {
      this.printSwitch = true
    }
    initPostHeight('congressional-district-profiles')
  },
  
  methods: {

    async getServiceAreasPerDistrict(cd_fips) {
      const resp = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/crosswalks/serv_areas_per/congressional_district/119th/${cd_fips}.json`)
      .then(function (response) {
        return response.json()
      })
      .catch((err) => { 
        console.log(err) 
      })
      return resp
    },

    async initGetData () {
      // Determine the associated grantees
      this.servAreas = await this.getServiceAreasPerDistrict(this.selectedCDFips)

      // // Get data object for this state profile
      await this.getProfileData()

      // Get offices for each BF General grantee
      let officesPerGrantee = {}
      if (this.granteesByType.bfGeneral.length > 0) {
        for (const rno of this.granteesByType.bfGeneral.map(x => x.recipient_id)) {
          if (Object.keys(this.profileData.grantees).indexOf(rno) > -1) { // Filter out any grantees that rounded to serving 0.0% of constituents
            officesPerGrantee[rno] = await this.getOffices(rno)
          } 
        }
      }

      this.officesPerGrantee = officesPerGrantee
      return
    },

    async getProfileData () {
      if (!this.areaIsSelected) return

      this.loading = true
      let route = new URL(`${this.apiRoute}/public/project/congressional-district-profiles/${this.selectedCDFips}/data.json`)
      
      let d = await fetch(route, {method: 'GET', redirect: 'follow'})
        .then((response) => response.json())
        .catch((err) => {
          console.log(err)
          this.profileData = null
        })
      
      if (d && d !== undefined && d!== null) { 
        // Filter out any grantees that rounded to 0.0% of the district's population (tiny tract slivers)
        for(let g in d.grantees) {
          if(d.grantees[g].percent_of_constituents === 0) {
            delete d.grantees[g]
          }
        }

        this.profileData = d
        
        if ('last_updated' in d) {
          this.updated = `${d['last_updated'].substring(5,7)}/${d['last_updated'].substring(8,10)}/${d['last_updated'].substring(0,4)}`
        }
      }

      this.loading = false
      return
    },

    async getOffices (rno) {
      let data = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/grantee/${rno}/offices.json`)
      .then(function(response) { 
        return response.json() 
      })
      .catch( err => { console.log(err) })
      return data
    },

    handleCdChange ({cd, label}) {
      this.selectedCDFips = cd
      this.selectedCDLabel = label
      if (cd) { 
        this.selectedState = cd.substring(0,2)
        window.top.postMessage({ district: cd }, "*")
      }
    },

    handleStateChange (st) {
      this.selectedCDFips = null
      this.selectedCDLabel = null
      this.selectedState = st
    },

    nth(n){
      return["st","nd","rd"][((n+90)%100-10)%10-1]||"th"
    },
  },

  watch: {
    selectedCDFips (to) {
      if (to) {
        this.initGetData()
      }
    },
    $route: {
      immediate: true,
      handler (to) {
        if (to.name !== null) { // ignore $route before full path is ready
          if (to.query.embed && to.query.embed === "true") {
            this.embedded = true
          } else {
            this.embedded = false
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#print-version-switch {
  display: flex;
  justify-content: flex-end;
}
.no-selection-text {
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 0.625rem;
  background-color: #daeefd;
  font-family: Lato, sans-serif;
  font-size: 1.25rem;

  ul {
    margin-top: 0.25rem;
    font-size: 1rem;
  }
  
  li {
    padding: 0.325rem 0;
  }
}
</style>